import { useState, useCallback } from "react";
import { useFormik } from "formik";
import classNames from "classnames";
import * as Yup from "yup";

import { AscModalNewMemberPropsI } from "./AscModalNewMember.type";
import AscModal from "../AscModal/AscModal";
import AscButton from "../AscButton/AscButton";
import AscTooltip from "../AscTooltip/AscTooltip";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import AscTextInput from "../AscInputFields/AscTextInput/AscTextInput";
import AscFilesUploader from "../AscFilesUploader/AscFilesUploader";

import { AdminEditPictureIcon, AdminImageIcon } from "../AscIcons";

import styles from "./AscModalNewMember.module.scss";

export const TEAM_MEMBER_ROLES = [
  { id: "SCHOOL", title: "School admin" },
  { id: "TEACHER", title: "Teacher" },
];

function AscModalNewMember({
  visible,
  setVisible,
  onAddNewTeamMember,
  image,
}: AscModalNewMemberPropsI) {
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [updoadModal, setIsModalOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      image: null,
      role: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      image: Yup.mixed().required("Image is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      role: Yup.string().required("Role is required"),
    }),
    onSubmit: (values) => onAddNewTeamMember(values, () => closeModal()),
  });

  function closeModal() {
    formik.resetForm();
    setVisible(false);
  }

  const handleUpload = useCallback(
    (acceptedFiles: File[]) => {
      const newUrl = URL.createObjectURL(acceptedFiles[0]);
      setThumbnailUrl(newUrl);

      formik.setFieldValue("image", acceptedFiles[0]);
      setIsModalOpen(false);
    },
    [formik]
  );

  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      modalClassName={styles.modalClassName}
      withBackground={false}
      onClose={closeModal}
    >
      <form
        onSubmit={formik.handleSubmit}
        className={styles.ground}
        data-testid="asc-modal-new-member"
      >
        <span className={styles.header}>New member</span>
        <div>
          <div className={styles.imageGround}>
            {image || thumbnailUrl ? (
              <img
                src={image || thumbnailUrl}
                alt="new-member-avatar"
                className={styles.image}
              />
            ) : (
              <AdminImageIcon className={styles.picture} />
            )}
            <div
              className={styles.editPictureBlock}
              onClick={() => setIsModalOpen(true)}
            >
              <AscTooltip
                tooltipAnchor={
                  <AdminEditPictureIcon className={styles.editPicture} />
                }
                children="Click to upload an avatar"
              />
            </div>
          </div>
          {formik.errors?.image && formik.touched?.image && (
            <div className={styles.fieldError}>
              {formik.errors?.image as string}
            </div>
          )}
        </div>
        <div className={styles.inputGround}>
          <div className={styles.inputField}>
            <label htmlFor="role" className={styles.inputLabel}>
              Position <span className={styles.inputLabelRequired}>*</span>
            </label>
            <div className={styles.circle} />
            <AscSelectInput
              className={classNames(styles.input, styles.inputPosition, {
                [styles.errorInput]: Boolean(formik.errors?.role),
              })}
              id="role"
              name="role"
              fullWidth={true}
              placeholder="Select a position..."
              onChange={(val) => formik.setFieldValue("role", val)}
              value={formik.values.role}
            >
              {TEAM_MEMBER_ROLES?.map((role) => (
                <AscSelectInput.Option
                  key={role.id}
                  value={role.id}
                  label={role.title}
                />
              ))}
            </AscSelectInput>
            {formik.errors?.role && (
              <div className={styles.fieldError}>
                {formik.errors?.role as string}
              </div>
            )}
          </div>

          <div className={styles.inputField}>
            <label htmlFor="email" className={styles.inputLabel}>
              Email <span className={styles.inputLabelRequired}>*</span>
            </label>
            <AscTextInput
              className={classNames(styles.input, {
                [styles.errorInput]:
                  Boolean(formik.errors?.email) && formik.touched?.email,
              })}
              id="email"
              name="email"
              placeholder="Enter email..."
              onChange={formik.handleChange}
              onBlur={(e) => formik.handleBlur(e)}
              value={formik.values.email}
            />
            {formik.errors?.email && formik.touched?.email && (
              <div className={styles.fieldError}>{formik.errors?.email}</div>
            )}
          </div>

          <div className={styles.inputField}>
            <label htmlFor="html" className={styles.inputLabel}>
              Name <span className={styles.inputLabelRequired}>*</span>
            </label>
            <AscTextInput
              className={classNames(styles.input, styles.inputName, {
                [styles.errorInput]:
                  Boolean(formik.errors?.name) && formik.touched?.name,
              })}
              id="name"
              name="name"
              placeholder="Enter name..."
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
            />
            {formik.errors?.name && formik.touched?.name && (
              <div className={styles.fieldError}>{formik.errors?.name}</div>
            )}
          </div>

          <div className={styles.buttonGround}>
            <AscButton
              className={styles.button}
              variant="secondary"
              onClick={closeModal}
            >
              Cancel
            </AscButton>
            <AscButton className={styles.button} type="submit">
              Add Member
            </AscButton>
          </div>
        </div>
      </form>
      {updoadModal && (
        <AscModal
          visible={updoadModal}
          onClose={() => setIsModalOpen(false)}
          innerContentClassName={styles.modalContent}
        >
          <AscFilesUploader
            name="image"
            type="image"
            accept={{
              "image/*": [".jpeg", ".png", ".jpg"],
            }}
            multiple={false}
            onDrop={handleUpload}
            srcFile=""
            className={styles.image}
          />
        </AscModal>
      )}
    </AscModal>
  );
}

export default AscModalNewMember;
